<script lang="ts" module>
  // Read-aloud script block — the design-system PRIMARY reading surface
  // (wiki/feature-design-system.md §Component primitives + §Typography, AC4).
  //
  // This is the content the user is performing, so it is the visual PRIMARY on
  // a practice screen (wiki §Hierarchy principle): Be Vietnam Pro 19px / line-height
  // 1.6, generous, legible. The currently-spoken line reads in full-weight ink;
  // a highlighted phrase within it is accent-tinted + underlined. The live cue
  // outranks NOTHING here — it signals via colour, not size.
  export interface ScriptLine {
    /** Stable key for the each-block. */
    id: string;
    /** Paragraph / line text. If `highlight` is set, that substring is marked. */
    text: string;
    /** The line currently being read — rendered in full-weight ink. */
    active?: boolean;
    /** Optional substring of `text` to accent-highlight (the script highlight). */
    highlight?: string;
  }
</script>

<script lang="ts">
  import { t } from '$lib/i18n';
  import { UiKeys } from '@voicedojo/backend-types';

  interface Props {
    /** Eyebrow label; omit for the localized "read aloud". */
    label?: string | null;
    /** The script lines. */
    lines: ScriptLine[];
    /** Poem layout — tighter, line-by-line (default false = story paragraphs). */
    isPoem?: boolean;
    class?: string;
  }

  let {
    label = null,
    lines,
    isPoem = false,
    class: klass = '',
  }: Props = $props();

  // Split a line's text around its highlight substring so the marked phrase can
  // carry the accent treatment without dangerouslySetInnerHTML.
  function parts(line: ScriptLine): { pre: string; mark: string; post: string } {
    if (!line.highlight) return { pre: line.text, mark: '', post: '' };
    const i = line.text.indexOf(line.highlight);
    if (i < 0) return { pre: line.text, mark: '', post: '' };
    return {
      pre: line.text.slice(0, i),
      mark: line.highlight,
      post: line.text.slice(i + line.highlight.length),
    };
  }
</script>

<section class="vd-script {klass}" class:vd-script--poem={isPoem}>
  <p class="vd-script__lbl">{label ?? t(UiKeys.PracticeView.ReadAloud)}</p>
  {#each lines as line (line.id)}
    {@const p = parts(line)}
    <p class="vd-script__line" class:vd-script__line--active={line.active}>
      {p.pre}{#if p.mark}<mark class="vd-script__mark">{p.mark}</mark>{/if}{p.post}
    </p>
  {/each}
</section>

<style lang="scss">
  .vd-script {
    margin: 0;
  }

  .vd-script__lbl {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-mute);
    margin: 0 0 14px;
  }

  // PRIMARY reading content — Be Vietnam Pro 19/1.6, soft ink by default.
  .vd-script__line {
    font-family: var(--font-ui);
    font-size: var(--text-script);
    font-weight: 400;
    line-height: var(--text-script--line-height);
    color: var(--ink-soft);
    margin: 0;

    & + & {
      margin-top: 12px;
    }
  }

  // The line being read — full-weight ink so the eye finds its place.
  .vd-script__line--active {
    color: var(--ink);
    font-weight: 700;
  }

  // A highlighted phrase — accent colour + underline, no background swatch.
  .vd-script__mark {
    background: none;
    color: var(--accent-text);
    font-weight: 700;
    border-bottom: 1px solid var(--accent);
  }

  .vd-script--poem .vd-script__line {
    line-height: 1.7;

    & + & {
      margin-top: 0;
    }
  }
</style>
